/**
 * Pedir el enlace para cambiar la contraseña.
 *
 * La respuesta es la misma exista o no la cuenta —el contrato dice que la
 * API siempre contesta 202— y la pantalla la respeta: si dijera «ese correo
 * no existe» convertiría este formulario en un buscador de cuentas.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Field } from '../components/Field';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSending(true);
    try {
      await api.requestPasswordReset({ email: email.trim() });
      setSent(true);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout>
        <h1>{t('forgot.doneTitle')}</h1>
        <Alert kind="success">{t('forgot.doneBody')}</Alert>
        <div className="links">
          <Link to="/login">{t('common.backToLogin')}</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1>{t('forgot.title')}</h1>
      <p className="lead">{t('forgot.lead')}</p>
      {error !== null && <Alert kind="error">{error}</Alert>}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field id="forgot-email" label={t('forgot.email')}>
          {(control) => (
            <input
              {...control}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <button type="submit" disabled={email.trim() === '' || sending}>
          {sending ? t('common.sending') : t('forgot.submit')}
        </button>
      </form>

      <div className="links">
        <Link to="/login">{t('common.backToLogin')}</Link>
      </div>
    </AuthLayout>
  );
}
